import sha256 from 'crypto-js/sha256';
import storage from 'local-storage-fallback';
import { CustomError, isNetworkError, TechnicalError } from '../../../common/errors';
import Log from '../../../common/logging';
import Result from '../../../common/result';
import secureIdentityProvider, { ScopeRequest, ScopeRequestStatus } from '../../../services/secureIdentityProvider';

export type LoadScopeRequest = {
  scopeRequestTemplate: string;
  network: string;
  did?: string;
  appId?: string; // This is SIP's internal partner ID, and will always be the civic-pay app when using cpass.
  cpassPartnerId?: string; // This is the externally-managed partner, i.e. the one from the GKNS which is currently the auth0 org ID.
  wallet?: string;
  credentialsIssuerDid?: string;
  timeout?: number;
};

const generateStorageKeyForScopeRequest = (storageRequest: LoadScopeRequest): string =>
  sha256(JSON.stringify(storageRequest)).toString();

const validStatuses = [
  ScopeRequestStatus.AWAITING_USER,
  ScopeRequestStatus.USER_ACKNOWLEDGED,
  ScopeRequestStatus.VERIFICATION_SUCCESS,
];

/** Try persist scope request id in storage, do not fail */
const storeScopeRequest = (storageRequest: LoadScopeRequest, scopeRequestId: string) => {
  try {
    storage.setItem(generateStorageKeyForScopeRequest(storageRequest), scopeRequestId);
  } catch (error) {
    Log.error('Error setting item from storage: ', error as Error);
  }
};

/** Try persist scope request id in storage, do not fail */
const clearScopeRequest = async (storageRequest: LoadScopeRequest) => {
  try {
    storage.removeItem(generateStorageKeyForScopeRequest(storageRequest));
  } catch (error) {
    Log.error('Error setting item from storage: ', error as Error);
  }
};

/** try load scope request from storage, fallback to no storage on error */
const loadScopeRequest = (storageRequest: LoadScopeRequest): string | null => {
  try {
    return storage.getItem(generateStorageKeyForScopeRequest(storageRequest));
  } catch (error) {
    Log.error('Error getting item from storage: ', error as Error);
    return null;
  }
};

/** Fetch a stored scope request by checking if there is a scope request id in storage, fetching the scope request, and ensuring it still has a valid status */
const fetchStoredScopeRequest = async (
  storageRequest: LoadScopeRequest,
): Promise<Result<CustomError, ScopeRequest | null>> => {
  const scopeRequestIdFromStorage = loadScopeRequest(storageRequest);
  if (!scopeRequestIdFromStorage) {
    return Result.successful(null);
  }

  const existingScopeRequest = await secureIdentityProvider.getScopeRequest(scopeRequestIdFromStorage);
  // if the scope request is not found, then it has expired and treat is as a no-cache result
  if (existingScopeRequest.isFailed() && existingScopeRequest.failedWith(isNetworkError(404))) {
    return Result.successful(null);
  }

  if (existingScopeRequest.isFailed()) {
    return Result.failed(
      new TechnicalError(
        `Unexpected error fetching saved scope request ${scopeRequestIdFromStorage}`,
        existingScopeRequest.failure(),
      ),
    );
  }

  const scopeRequest = existingScopeRequest.success();
  // use the scope request if it still has a valid status
  return validStatuses.includes(scopeRequest.status) ? Result.successful(scopeRequest) : Result.successful(null);
};

const deleteScopeRequest = async (request: LoadScopeRequest): Promise<void> => {
  await clearScopeRequest(request);
};

/** Load an existing scope request or create a new one */
const loadOrCreateScopeRequest = async (
  request: LoadScopeRequest,
  userAuthenticated: boolean,
): Promise<Result<CustomError, ScopeRequest>> => {
  // fetch existing scope request only if user is currently authenticated, as the call can only be made with an auth token
  const existingResult = userAuthenticated
    ? await fetchStoredScopeRequest(request)
    : Result.successful<CustomError, null>(null);
  if (existingResult.isFailed()) {
    return Result.failed(existingResult.failure());
  }

  const storedScopeRequest = existingResult.success();
  if (storedScopeRequest) {
    return Result.successful(storedScopeRequest);
  }

  // create a new scope request if no valid existing scope request found
  const createResult = await secureIdentityProvider.createScopeRequest({
    template: request.scopeRequestTemplate,
    cpassPartnerId: request.cpassPartnerId,
    appId: request.appId, // This is SIP's internal partner ID, and will always be the civic-pay app when using cpass.
    wallet: request.wallet, // This is the externally-managed partner, i.e. the one from the GKNS which is currently the auth0 org ID.
    did: request.did,
    credentialsIssuerDid: request.credentialsIssuerDid,
    timeout: request.timeout,
  });

  // store scope request id
  if (createResult.isSuccessful()) {
    storeScopeRequest(request, createResult.success().uuid);
  }

  return createResult;
};

const loadExistingScopeRequestByUuid = async (uuid: string): Promise<Result<CustomError, ScopeRequest>> => {
  return secureIdentityProvider.getScopeRequest(uuid);
};

export default {
  fetchStoredScopeRequest,
  loadOrCreateScopeRequest,
  loadExistingScopeRequestByUuid,
  deleteScopeRequest,
};
